import { useCallback, useSyncExternalStore } from 'react';

// Shared plumbing for the app's module-scope, document-keyed stores.
//
// State must survive tab navigation — NoticeViewer is a React Router route, so
// leaving a tab unmounts its components (and their PdfViewer / PDF.js document).
// Keeping state at module scope, keyed by document id, lets a run keep going and
// its results persist while the user is elsewhere. React reads it via
// useSyncExternalStore. Both factories below centralize the identical
// subscribe / snapshot / notify wiring so each store only writes its own logic.

// Every store made here, so `resetDoc` can make all of them re-read at once.
//
// A store caches its document's answers in module memory, which is the point of it —
// until what those answers are a view OF changes underneath. Switching runs replaces
// every answer in one go (see docState), and a store still holding the last run's
// snapshot would go on showing it, forever, with no way to know it was stale.
const resetters = new Set<(docId: string) => void>();

// Re-read one document in every store. The listeners survive — components stay
// subscribed to the store they subscribed to; only what it says changes.
export function resetDoc(docId: string) {
  resetters.forEach((reset) => reset(docId));
}

// A simple one-value-per-document store (e.g. the selected part, the current
// step index, a refresh epoch). `set` no-ops when the value is unchanged.
export function createDocValueStore<T>(initial: T) {
  const values = new Map<string, T>();
  const listeners = new Map<string, Set<() => void>>();

  const subs = (docId: string): Set<() => void> => {
    let set = listeners.get(docId);
    if (!set) {
      set = new Set();
      listeners.set(docId, set);
    }
    return set;
  };

  const get = (docId: string): T => (values.has(docId) ? (values.get(docId) as T) : initial);

  const set = (docId: string, value: T) => {
    if (get(docId) === value) return;
    values.set(docId, value);
    subs(docId).forEach((l) => l());
  };

  const useValue = (docId: string): T => {
    const subscribe = useCallback(
      (cb: () => void) => {
        const s = subs(docId);
        s.add(cb);
        return () => {
          s.delete(cb);
        };
      },
      [docId],
    );
    const getSnapshot = useCallback(() => get(docId), [docId]);
    return useSyncExternalStore(subscribe, getSnapshot);
  };

  // Drop the value so the next read falls back to `initial` — for the stores that
  // lazily hydrate themselves (see videoSelection), that IS the re-read.
  resetters.add((docId) => {
    if (!values.has(docId)) return;
    values.delete(docId);
    subs(docId).forEach((l) => l());
  });

  return { get, set, useValue };
}

// A richer per-document record: a `snapshot` (the reactive state) plus optional
// non-reactive `Extra` fields (e.g. the index of the run being filled). Each store
// supplies how to build a document's initial entry and
// keeps its own `update`/mutation logic; this factory owns the entries map, lazy
// creation, subscription, and the read hook. Call `notify(docId)` after mutating
// a snapshot to re-render subscribers.
export function createDocEntryStore<S extends object, Extra extends object = object>(
  makeInitial: (docId: string) => { snapshot: S } & Extra,
) {
  type Entry = { snapshot: S; listeners: Set<() => void> } & Extra;
  const entries = new Map<string, Entry>();

  const getEntry = (docId: string): Entry => {
    let entry = entries.get(docId);
    if (!entry) {
      entry = { ...makeInitial(docId), listeners: new Set<() => void>() } as Entry;
      entries.set(docId, entry);
    }
    return entry;
  };

  // Rebuild the entry from `makeInitial` — which re-reads docState — and keep the
  // listeners, by assigning INTO the existing entry rather than replacing it. Replace
  // it and every component subscribed to this document is subscribed to an object
  // nothing will ever notify again.
  //
  // Only for a document this store has actually seen: building an entry here for one
  // it has not would be reading state for a phase nobody has opened.
  resetters.add((docId) => {
    const entry = entries.get(docId);
    if (!entry) return;
    Object.assign(entry, makeInitial(docId));
    entry.listeners.forEach((l) => l());
  });

  const notify = (docId: string) => {
    getEntry(docId).listeners.forEach((l) => l());
  };

  // Merge a partial into a document's snapshot and re-render its subscribers — the
  // one mutation every entry store does. Each used to hand-roll this identical helper;
  // now they share it. (A store needing more than a merge, e.g. categoryRun's mirror
  // step, still uses getEntry/notify directly.)
  const patch = (docId: string, partial: Partial<S>) => {
    const entry = getEntry(docId);
    entry.snapshot = { ...entry.snapshot, ...partial };
    entry.listeners.forEach((l) => l());
  };

  const get = (docId: string): S => getEntry(docId).snapshot;

  const useSnapshot = (docId: string): S => {
    const subscribe = useCallback(
      (cb: () => void) => {
        const entry = getEntry(docId);
        entry.listeners.add(cb);
        return () => {
          entry.listeners.delete(cb);
        };
      },
      [docId],
    );
    const getSnapshot = useCallback(() => getEntry(docId).snapshot, [docId]);
    return useSyncExternalStore(subscribe, getSnapshot);
  };

  return { getEntry, notify, patch, get, useSnapshot };
}
